import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '../../auth/hooks/useAuth'
import { ShoppingBag, CreditCard, RotateCcw, Gift, Users } from 'lucide-react'
import AuthModal from '../../auth/components/AuthModal'
import ProfileSidebar from '../../../components/profile/ProfileSidebar'
import ProfileUpdateForm from '../../../components/profile/ProfileUpdateForm'
import TransactionHistory from '../../../components/profile/TransactionHistory'
import PurchasedPage from '../../order/pages/PurchasedPage'
import CtvBenefitsHistory, { type CtvBenefitTransaction } from '../../../components/profile/CtvBenefitsHistory'
import ReferralTab from '../../../components/profile/ReferralTab'
import { apiGetJson } from '../../../lib/apiClient'
import { unwrapList } from '../../../lib/apiUnwrap'
import { motion } from 'framer-motion'
import { fadeIn, slideInLeft } from '../../../lib/animations'

// Types (can be moved to a shared types file)
type TransactionWithDetails = {
  id: string
  user_id: string
  created_at: string
  amount: number
  type: 'top_up' | 'purchase'
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded'
  metadata?: {
    guide_url?: string
    quantity_in_order?: number
    order_index?: number
    payment_method?: string
    transfer_content?: string
    is_manual_delivery?: boolean
  }
  product_variants?: {
    name: string
    guide_url?: string
    products?: {
      name: string
      guide_url?: string
    }
  }
  product_keys?: {
    key_value: string
  }
}

type ProfileTabId = 'purchases' | 'topups' | 'ctv'
type CtvSubTabId = 'benefits' | 'referral'

type ReferredUser = {
  id: string
  email: string
  full_name?: string
  created_at: string
}

type ReferralStats = {
  totalReferrals: number
  monthlyEarnings: number
  totalEarnings: number
}

const TAB_HASH_BY_ID: Record<ProfileTabId, string> = {
  purchases: 'purchases',
  topups: 'topups',
  ctv: 'ctv',
}

const TAB_ID_BY_HASH: Record<string, ProfileTabId> = {
  purchases: 'purchases',
  topups: 'topups',
  ctv: 'ctv',
  'ctv-benefits': 'ctv',
}

export default function ProfilePage() {
  const { user, session, isInitializing, refreshProfile } = useAuth()
  const [activeTab, setActiveTab] = useState<ProfileTabId>('purchases')
  const [activeCtvSubTab, setActiveCtvSubTab] = useState<CtvSubTabId>('benefits')
  const [purchasesViewKey, setPurchasesViewKey] = useState(0)
  const tabs = [
    { id: 'purchases', label: 'Lịch sử mua hàng', icon: ShoppingBag },
    { id: 'topups', label: 'Lịch sử nạp', icon: CreditCard },
    { id: 'ctv' as const, label: 'CTV & Giới thiệu', icon: Gift },
  ]

  useEffect(() => {
    const hash = window.location.hash.replace('#', '')
    if (hash && TAB_ID_BY_HASH[hash]) {
      setActiveTab(TAB_ID_BY_HASH[hash])
    }
  }, [])

  const handleTabClick = (tabId: ProfileTabId) => {
    setActiveTab(tabId)
    window.location.hash = TAB_HASH_BY_ID[tabId]
  }

  const [topupTransactions, setTopupTransactions] = useState<TransactionWithDetails[]>([])
  const [ctvBenefitTransactions, setCtvBenefitTransactions] = useState<CtvBenefitTransaction[]>([])
  const [referredUsers, setReferredUsers] = useState<ReferredUser[]>([])
  const [referralStats, setReferralStats] = useState<ReferralStats>({
    totalReferrals: 0,
    monthlyEarnings: 0,
    totalEarnings: 0,
  })
  const [loading, setLoading] = useState(true)
  const [referralLoading, setReferralLoading] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)

  const fetchTopupHistory = useCallback(async () => {
    if (!user) return
    setLoading(true)
    try {
      const resp = await apiGetJson<any>('/api/transactions?type=top_up&limit=200')
      setTopupTransactions(unwrapList<TransactionWithDetails>(resp))
    } catch (error) {
      console.error('Error fetching history:', error)
    } finally {
      setLoading(false)
    }
  }, [user])

  const fetchCtvBenefitsHistory = useCallback(async () => {
    if (!user?.is_ctv) {
      setCtvBenefitTransactions([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const resp = await apiGetJson<any>('/api/transactions?type=purchase&limit=200')
      const purchaseTransactions = unwrapList<CtvBenefitTransaction>(resp)
      const ctvTransactions = purchaseTransactions.filter(tx => {
        const breakdown = tx.metadata?.discount_breakdown
        const ctvPercent = Number(breakdown?.ctv || 0)
        const discountSource = String(tx.discount_source || tx.metadata?.discount_source || breakdown?.applied_source || '').toUpperCase()
        const commissionAmount = Number(tx.commission_amount || tx.metadata?.commission_amount || 0)
        return ctvPercent > 0 || discountSource === 'USER' || commissionAmount > 0
      })
      setCtvBenefitTransactions(ctvTransactions)
    } catch (error) {
      console.error('Error fetching ctv benefits history:', error)
    } finally {
      setLoading(false)
    }
  }, [user])

  const fetchReferralData = useCallback(async () => {
    if (!user) return
    setReferralLoading(true)
    try {
      const resp = await apiGetJson<any>('/api/profile/referrals')
      const data = resp?.data && typeof resp.data === 'object' ? resp.data : {}
      const rawUsers = Array.isArray(data.referred_users) ? data.referred_users : []
      const normalizedUsers = rawUsers
        .map((item: any) => ({
          id: String(item?.id || ''),
          email: String(item?.email || ''),
          full_name: item?.full_name ? String(item.full_name) : undefined,
          created_at: String(item?.created_at || ''),
        }))
        .filter((item: ReferredUser) => item.id && item.email)

      const totalReferralsRaw = Number(data.total_referrals)
      const monthlyEarningsRaw = Number(data.monthly_earnings)
      const totalEarningsRaw = Number(data.total_earnings)

      setReferredUsers(normalizedUsers)
      setReferralStats({
        totalReferrals: Number.isFinite(totalReferralsRaw) ? Math.max(0, Math.round(totalReferralsRaw)) : normalizedUsers.length,
        monthlyEarnings: Number.isFinite(monthlyEarningsRaw) ? Math.max(0, Math.round(monthlyEarningsRaw)) : 0,
        totalEarnings: Number.isFinite(totalEarningsRaw) ? Math.max(0, Math.round(totalEarningsRaw)) : 0,
      })
    } catch (error) {
      console.error('Error fetching referral data:', error)
      setReferredUsers([])
      setReferralStats({
        totalReferrals: 0,
        monthlyEarnings: 0,
        totalEarnings: 0,
      })
    } finally {
      setReferralLoading(false)
    }
  }, [user])

  const handleRefresh = () => {
    if (activeTab === 'purchases') {
      setPurchasesViewKey(prev => prev + 1)
      return
    }
    if (activeTab === 'ctv') {
      if (activeCtvSubTab === 'benefits') {
        void fetchCtvBenefitsHistory()
      } else {
        void fetchReferralData()
      }
      return
    }
    void fetchTopupHistory()
  }

  useEffect(() => {
    if (isInitializing) return

    if (!session) {
      setShowAuthModal(true)
      setLoading(false)
      return
    }

    setShowAuthModal(false)

    if (user && activeTab === 'topups') {
      fetchTopupHistory()
      return
    }
    if (user && activeTab === 'ctv') {
      if (activeCtvSubTab === 'benefits') {
        fetchCtvBenefitsHistory()
      } else {
        fetchReferralData()
      }
      return
    }

    setLoading(false)
    setReferralLoading(false)
  }, [user, session, activeTab, activeCtvSubTab, isInitializing, fetchTopupHistory, fetchCtvBenefitsHistory, fetchReferralData])

  const referralUser = useMemo(() => {
    if (!user) return null
    return {
      ...user,
      referred_by: user.referrer_id || undefined,
      referral_count: referralStats.totalReferrals,
    }
  }, [user, referralStats.totalReferrals])

  if (!session || !user) {
    return (
      <>
        <div className="flex items-center justify-center h-96">
          <p className="text-gray-500">Vui lòng đăng nhập để xem thông tin cá nhân</p>
        </div>
        {showAuthModal && <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />}
      </>
    )
  }

  if (
    (activeTab === 'topups' && loading && topupTransactions.length === 0)
    || (activeTab === 'ctv' && activeCtvSubTab === 'benefits' && loading && ctvBenefitTransactions.length === 0)
  ) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-8">
        {/* Sidebar */}
        <motion.div className="lg:col-span-1 space-y-6" {...slideInLeft(0.1)}>
          <ProfileSidebar user={user} onRefreshProfile={refreshProfile} />
          <ProfileUpdateForm user={user} onRefreshProfile={refreshProfile} />
        </motion.div>

        {/* Main Content */}
        <motion.div className="lg:col-span-3" {...fadeIn(0.2)}>
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border dark:border-gray-800 overflow-hidden">
            {/* Tabs */}
            <div className="border-b dark:border-gray-800 flex items-center justify-between pr-4 bg-gray-50 dark:bg-gray-900/50">
              <div className="overflow-x-auto scrollbar-hide">
                <nav className="flex -mb-px min-w-max sm:min-w-0">
                  {tabs.map(tab => (
                    <motion.button
                      key={tab.id}
                      whileTap={{ scale: 0.96 }}
                      onClick={() => handleTabClick(tab.id as ProfileTabId)}
                      className={`py-3 sm:py-4 px-4 sm:px-6 text-xs sm:text-sm font-medium border-b-2 flex items-center space-x-1 sm:space-x-2 whitespace-nowrap cursor-pointer transition-colors ${activeTab === tab.id
                        ? 'border-amber-500 text-amber-600 bg-white dark:bg-gray-900'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                        }`}
                    >
                      <tab.icon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                      <span>{tab.label}</span>
                    </motion.button>
                  ))}
                </nav>
              </div>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleRefresh}
                className="flex items-center gap-1 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-amber-600 transition-colors px-3 py-1.5 rounded-full hover:bg-white dark:hover:bg-gray-800 border border-transparent hover:border-gray-200 dark:hover:border-gray-700 shadow-sm"
                title="Làm mới dữ liệu"
              >
                <RotateCcw className={`h-3 w-3 sm:h-4 sm:w-4 ${activeTab !== 'purchases' && loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Làm mới</span>
              </motion.button>
            </div>

            {/* Content */}
            <div className="p-6">
              {activeTab === 'purchases' ? (
                <PurchasedPage key={purchasesViewKey} embedded />
              ) : activeTab === 'ctv' ? (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={() => setActiveCtvSubTab('benefits')}
                      className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors ${activeCtvSubTab === 'benefits'
                        ? 'bg-white dark:bg-gray-900 text-amber-700 dark:text-amber-400 shadow-sm border border-amber-100 dark:border-amber-800'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                        }`}
                    >
                      Lịch sử ưu đãi CTV
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveCtvSubTab('referral')}
                      className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors ${activeCtvSubTab === 'referral'
                        ? 'bg-white dark:bg-gray-900 text-amber-700 dark:text-amber-400 shadow-sm border border-amber-100 dark:border-amber-800'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                        }`}
                    >
                      Tab giới thiệu
                    </button>
                  </div>

                  {activeCtvSubTab === 'referral' && referralStats.totalReferrals === 0 ? (
                    <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 p-4 text-amber-800 dark:text-amber-200">
                      <div className="flex items-start gap-2">
                        <Users className="h-5 w-5 mt-0.5 flex-shrink-0" />
                        <div className="text-sm">
                          Hãy mời ít nhất 1 người đầu tiên để bắt đầu chuỗi giới thiệu và mở rộng tệp khách hàng của bạn.
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {activeCtvSubTab === 'benefits' ? (
                    <CtvBenefitsHistory
                      user={user}
                      transactions={ctvBenefitTransactions}
                      loading={loading}
                    />
                  ) : referralUser ? (
                    <ReferralTab
                      user={referralUser}
                      referralStats={referralStats}
                      referredUsers={referredUsers}
                      loading={referralLoading}
                    />
                  ) : null}
                </div>
              ) : (
                <TransactionHistory transactions={topupTransactions} />
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
